import { useEffect, useState } from "react";
import axios from "axios";
import { Download, PlayCircle, Phone, MessageSquareText, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE_TEL = "+12145004192";
const PHONE_DISPLAY = "214-500-4192";

const VIDEOS = [
  { title: "Introduction to LifeWorks", url: "https://youtu.be/qAx550NDzPw", embed: "https://www.youtube.com/embed/qAx550NDzPw" },
  { title: "LifeWorks Step 1", url: "https://youtu.be/poq_BWnq4Uc", embed: "https://www.youtube.com/embed/poq_BWnq4Uc" },
  { title: "LifeWorks Step 2", url: "https://youtu.be/yBvmwD1WPuo", embed: "https://www.youtube.com/embed/yBvmwD1WPuo" },
  { title: "LifeWorks Step 3", url: "https://youtu.be/_nBKfkbQCjk", embed: "https://www.youtube.com/embed/_nBKfkbQCjk" },
  { title: "LifeWorks MasterMind", url: "https://youtu.be/G3YIzKft2gA", embed: "https://www.youtube.com/embed/G3YIzKft2gA" },
];

const MODULES = [
  { n: 1, title: "Vision Exercise", file: "lifemastery-1-vision-exercise.pdf" },
  { n: 2, title: "Mission Exercise", file: "lifemastery-2-mission-exercise.pdf" },
  { n: 3, title: "Success Rituals", file: "lifemastery-3-success-rituals.pdf" },
  { n: 4, title: "Project Form", file: "lifemastery-4-project-form.pdf" },
  { n: 5, title: "Yearly Commitment Tracker", file: "lifemastery-5-yearly-commitment-tracker.pdf" },
  { n: 6, title: "Quarterly Commitments", file: "lifemastery-6-quarterly-commitments.pdf" },
  { n: 7, title: "Weekly Commitments", file: "lifemastery-7-weekly-commitments.pdf" },
  { n: 8, title: "My Bucket", file: "lifemastery-8-my-bucket.pdf" },
  { n: 9, title: "My Word", file: "lifemastery-9-my-word.pdf" },
  { n: 10, title: "The System at a Glance", file: "lifemastery-10-infograph.pdf" },
  { n: 11, title: "LMD NOW Exercise", file: "lifemastery-11-lmd-now-exercise.pdf" },
  { n: 12, title: "Journal Form", file: "lifemastery-12-journal-form.pdf" },
  { n: 13, title: "Commit to Use the System", file: "lifemastery-13-commit-to-use-the-system.pdf" },
  { n: 14, title: "The LifeWorks Creed", file: "lifemastery-14-lifeworks-creed.pdf" },
  { n: 15, title: "The Goose Story", file: "lifemastery-16-goose-story.pdf" },
];

function BuyerCheckin() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        interest: "buyer",
        message: "DIY buyer check-in from materials page",
        consent: true,
      });
      setState("done");
    } catch {
      setState("error");
    }
  };

  const inputCls = "w-full bg-[#FDFBF7] border border-[#E2D9CC] rounded-xl px-4 py-3 text-sm text-[#231F20] placeholder-[#A79B8D] focus:outline-none focus:border-[#C85A32] transition-colors";

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-8" data-testid="buyer-checkin-section">
      <div className="rounded-2xl border-2 border-[#C85A32] bg-white p-6 sm:p-8">
        {state === "done" ? (
          <div className="flex items-start gap-3" data-testid="buyer-checkin-success">
            <CheckCircle2 size={22} className="text-[#C85A32] shrink-0 mt-0.5" />
            <div>
              <p className="font-serif-accent font-semibold text-lg text-[#231F20]">You're in — check your inbox.</p>
              <p className="text-sm text-[#6E6259] mt-1 leading-relaxed">Your welcome note is on its way, with a few short tips coming over the next two weeks. Now scroll down and start with the Introduction video.</p>
            </div>
          </div>
        ) : (
          <>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-2">Just joined?</p>
            <p className="font-serif-accent font-semibold text-xl text-[#231F20] mb-1">Check in and get your welcome note.</p>
            <p className="text-sm text-[#6E6259] mb-5 leading-relaxed">So I know you're in and can send your welcome email plus a few short tips over the next two weeks to help you get the most out of the system.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="buyer-checkin-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="buyer-name-input" required minLength={2} placeholder="First and last name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                <input data-testid="buyer-email-input" required type="email" placeholder="Email you paid with" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </div>
              <label className="flex items-start gap-3 text-[11px] text-[#6E6259] leading-relaxed cursor-pointer select-none" data-testid="buyer-consent-label">
                <input data-testid="buyer-consent-checkbox" required type="checkbox" className="mt-0.5 w-4 h-4 shrink-0 rounded border-[#E2D9CC] accent-[#C85A32]" />
                <span>Email me my welcome note and short program tips over the next two weeks. Unsubscribe anytime.</span>
              </label>
              {state === "error" && (
                <p className="text-xs text-red-600" data-testid="buyer-checkin-error">Something went wrong — please try again, or text John at 214-500-4192.</p>
              )}
              <button data-testid="buyer-submit-btn" type="submit" disabled={state === "sending"}
                className="inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B04B25] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">
                {state === "sending" ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : "Start my welcome series"}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default function LifemasteryPage() {
  useEffect(() => {
    document.title = "LifeMastery — Your Materials | Thrive Beyond Service";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#231F20]" data-testid="lifemastery-page">
      <header className="border-b border-[#E2D9CC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C85A32] font-semibold">Thrive Beyond Service</p>
          <h1 className="font-serif-accent font-semibold text-3xl sm:text-4xl mt-2">Welcome to LifeMastery.</h1>
          <p className="text-[#6E6259] text-sm sm:text-base mt-3 leading-relaxed max-w-2xl">
            Everything in your Do-It-Yourself System lives on this page — bookmark it. Watch the videos first,
            then work the forms in order. Print what you can; pen and paper beats a screen for this work.
            More video sessions are on the way — I'll text you when they land. — John
          </p>
        </div>
      </header>

      <BuyerCheckin />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-14">
        <section data-testid="videos-section">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-2">Watch first</p>
          <h2 className="font-serif-accent font-medium text-2xl mb-6">The five foundation videos</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {VIDEOS.map((v, i) => (
              <div key={v.title} className="rounded-2xl border border-[#E2D9CC] bg-white overflow-hidden" data-testid={`video-card-${i + 1}`}>
                <div className="aspect-video bg-[#231F20]">
                  <iframe
                    src={v.embed}
                    title={v.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <p className="font-serif-accent font-semibold text-base">{v.title}</p>
                  <a href={v.url} target="_blank" rel="noreferrer" className="text-xs text-[#C85A32] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`video-link-${i + 1}`}>
                    <PlayCircle size={14} /> YouTube
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="modules-section">
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C85A32] font-semibold mb-2">Do the work</p>
          <h2 className="font-serif-accent font-medium text-2xl mb-6">Your worksheets & forms</h2>
          <div className="space-y-3">
            {MODULES.map((m) => (
              <div key={m.n} className="rounded-2xl border border-[#E2D9CC] bg-[#FAF7F2] px-5 py-4 flex items-center justify-between gap-4" data-testid={`module-${m.n}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#C85A32]/10 text-[#C85A32] font-mono-accent text-xs font-bold flex items-center justify-center">{m.n}</span>
                  <p className="font-serif-accent font-semibold text-base truncate">{m.title}</p>
                </div>
                <a
                  href={`/files/${m.file}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B04B25] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-200"
                  data-testid={`module-download-${m.n}`}
                >
                  <Download size={13} /> Open PDF
                </a>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-[#E2D9CC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-[#6E6259]"><span className="font-serif-accent font-semibold text-[#231F20]">Thrive Beyond Service</span> · by John Jones</p>
          <div className="flex gap-3">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 bg-[#C85A32] text-white text-xs font-semibold px-4 py-2 rounded-full" data-testid="lifemastery-call-btn"><Phone size={13} /> Call {PHONE_DISPLAY}</a>
            <a href={`sms:${PHONE_TEL}`} className="inline-flex items-center gap-2 border border-[#E2D9CC] text-[#443E3C] text-xs font-semibold px-4 py-2 rounded-full" data-testid="lifemastery-text-btn"><MessageSquareText size={13} /> Text John</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
